import {
  Component,
  computed,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { IconComponent } from '../../icons/icon.component';
import { OrderBookingModalComponent } from '../order-booking-modal/order-booking-modal.component';
import { AuthService } from '../../../core/services/auth/auth.service';
import { PrivilegeService } from '../../../core/services/privileges/privilege.service';
import { BreadcrumbService } from '../../../core/services/breadcrumb/breadcrumb.service';
import {
  ChildMenuItem,
  MenuItem,
  MenuGroup,
  SIDEBAR_MENU_ITEMS,
  isChildMenuItemVisible,
  getFilteredGroupedMenuItems,
} from './sidebar-menu.config';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterLink, IconComponent, OrderBookingModalComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();
  private routerSubscription?: Subscription;

  // Expand/collapse state for parent items
  expandedItems = new Set<string>();

  // Flyout state for collapsed sidebar hover
  hoveredItem: MenuItem | null = null;
  flyoutTop = 0;
  private flyoutHovered = false;
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;

  allMenuItems: MenuItem[] = SIDEBAR_MENU_ITEMS;

  groupedMenuItems = computed((): MenuGroup[] => {
    const user = this.authService.currentUser();
    return getFilteredGroupedMenuItems(
      this.allMenuItems,
      user,
      (privileges) => this.privilegeService.hasAnyPrivilege(privileges)
    );
  });

  private itemDelayMap = new Map<string, number>();

  getItemDelay(route: string): number {
    if (!this.itemDelayMap.has(route)) {
      const idx = this.allMenuItems.findIndex(m => m.route === route);
      this.itemDelayMap.set(route, Math.max(0, idx) * 26 + 35);
    }
    return this.itemDelayMap.get(route) ?? 35;
  }

  // ── Active state ──────────────────────────────────────────────────

  isMenuItemActive(route: string): boolean {
    const url = this.router.url.split('?')[0];
    if (route === '/patient-list' && url.startsWith('/patient-report')) return true;
    if (route === '/patient' && url.includes('/patient/patient-report')) return true;
    return url === route || url.startsWith(route + '/');
  }

  isChildActive(child: ChildMenuItem): boolean {
    const url = this.router.url.split('?')[0];
    const fullUrl = this.router.url;

    if (child.activeMatchSegment) {
      if (url.includes(child.activeMatchSegment) || fullUrl.includes(child.activeMatchSegment)) {
        return true;
      }
      if (child.queryParams?.['tab'] && fullUrl.includes(`tab=${child.queryParams['tab']}`)) {
        return true;
      }
      if (child.exactMatch && (url === child.route || url.startsWith(child.route + '/overview'))) {
        return true;
      }
      return false;
    }

    if (url === child.route) return true;
    if (child.exactMatch) return false;
    return url.startsWith(child.route + '/');
  }

  isParentActive(item: MenuItem): boolean {
    if (!item.children || item.children.length === 0) {
      return this.isMenuItemActive(item.route);
    }
    const url = this.router.url.split('?')[0];
    if (url === item.route || url.startsWith(item.route + '/')) {
      return true;
    }
    return item.children.some((c) => this.isChildActive(c));
  }



  isChildVisible(child: ChildMenuItem): boolean {
    const user = this.authService.currentUser();
    return isChildMenuItemVisible(
      child,
      user,
      (privileges) => this.privilegeService.hasAnyPrivilege(privileges)
    );
  }

  // ── Expand/collapse (Single-open accordion) ────────────────────────
  isExpanded(route: string): boolean { return this.expandedItems.has(route); }

  toggleExpand(route: string): void {
    if (this.expandedItems.has(route)) {
      this.expandedItems.delete(route);
    } else {
      this.expandedItems.clear();
      this.expandedItems.add(route);
    }
  }

  private autoExpandActiveParent(): void {
    for (const item of this.allMenuItems) {
      if (item.children?.some(c => this.isChildActive(c))) {
        this.expandedItems.clear();
        this.expandedItems.add(item.route);
        break;
      }
    }
  }

  // ── Flyout (collapsed hover) ──────────────────────────────────────

  onParentMouseEnter(event: MouseEvent, item: MenuItem): void {
    if (!this.collapsed || !item.children) return;
    if (this.closeTimeout) { clearTimeout(this.closeTimeout); this.closeTimeout = null; }
    this.flyoutTop = (event.currentTarget as HTMLElement).getBoundingClientRect().top;
    this.hoveredItem = item;
  }

  onParentMouseLeave(): void {
    if (!this.collapsed) return;
    this.closeTimeout = setTimeout(() => { if (!this.flyoutHovered) this.hoveredItem = null; }, 120);
  }

  onFlyoutMouseEnter(): void {
    this.flyoutHovered = true;
    if (this.closeTimeout) { clearTimeout(this.closeTimeout); this.closeTimeout = null; }
  }

  onFlyoutMouseLeave(): void { this.flyoutHovered = false; this.hoveredItem = null; }

  closeFlyout(): void { this.hoveredItem = null; this.flyoutHovered = false; }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.closeFlyout(); }

  // ── Sidebar toggle ────────────────────────────────────────────────

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
    if (!this.collapsed) this.closeFlyout();
  }

  @Input() mobileSidebarOpen = false;
  @Output() mobileSidebarClose = new EventEmitter<void>();
  closeMobileSidebar(): void { this.mobileSidebarClose.emit(); }

  showOrderBookingModal = false;
  onChildAction(child: ChildMenuItem): void {
    if (child.action === 'order-booking') this.showOrderBookingModal = true;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────

  constructor(
    private router: Router,
    public authService: AuthService,
    private privilegeService: PrivilegeService,
    private breadcrumbService: BreadcrumbService,
  ) { }

  ngOnInit(): void {
    this.autoExpandActiveParent();
    this.routerSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => { this.autoExpandActiveParent(); this.closeFlyout(); });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
  }
}
